import { isFuture, isToday, isTomorrow } from 'date-fns';

import { GeneratedGetDrawByProductIDsQuery } from '../../../types/generated';
import {
  isAuctionRelease,
  isAvailable,
  isBlockchainAuctionRelease,
  isDrawRelease,
  isInNext24Hours,
  isInNextSixDays,
  isPrivateRelease,
  isSoldOut,
  isTimedRelease,
  isUpcoming,
} from '../../../utils/product';

type Status = {
  key: string;
  isActive: boolean;
};

export const getProductTilePill = (
  product: Product,
  isPreOrder?: boolean,
  draw?: GeneratedGetDrawByProductIDsQuery['getDrawByProductIDs'][number],
): Status | null => {
  const isDrawOrAuction =
    isDrawRelease(product.releaseType) || isAuctionRelease(product.releaseType);

  if (product.date === undefined) return null;

  if (isSoldOut(product)) {
    return { key: 'tileStatus.soldOut', isActive: false };
  }

  if (isUpcoming(product) && !isDrawOrAuction) {
    return { key: 'tileStatus.upcoming', isActive: false };
  }

  if (
    isUpcoming(product) &&
    isDrawOrAuction &&
    isFuture(new Date(draw?.openAt || ''))
  ) {
    return { key: 'tileStatus.upcoming', isActive: false };
  }

  if (isUpcoming(product) && isDrawOrAuction) {
    if (isAuctionRelease(product.releaseType)) {
      return { key: 'tileStatus.auction.upcoming', isActive: true };
    }
    if (isPreOrder) {
      return { key: 'tileStatus.preOrder.upcoming', isActive: true };
    }
    return { key: 'tileStatus.draw.upcoming', isActive: true };
  }

  if (isPrivateRelease(product)) {
    if (product.fewLeft) {
      return { key: 'tileStatus.private.fewLeft', isActive: true };
    }
    return { key: 'tileStatus.private.default', isActive: true };
  }

  if (
    (isTimedRelease(product) || isBlockchainAuctionRelease(product)) &&
    product.closingDate
  ) {
    if (isAvailable(product)) {
      let statusKey = 'tileStatus.timed.active.long';

      if (isInNext24Hours(new Date(product.closingDate))) {
        statusKey = 'tileStatus.timed.active.short';
      }
      return { key: statusKey, isActive: true };
    }
  }

  if (
    isAvailable(product) &&
    !(isPrivateRelease(product) || isTimedRelease(product))
  ) {
    if (product.fewLeft) {
      return { key: 'tileStatus.available.fewLeft', isActive: true };
    }
    return { key: 'tileStatus.available.default', isActive: true };
  }

  return null;
};

export const getBannerStatus = (
  product: Product,
  isPreOrder?: boolean,
): Status | null => {
  if (product.date === undefined) return null;

  const isDrawOrAuction =
    isDrawRelease(product.releaseType) || isAuctionRelease(product.releaseType);

  if (isSoldOut(product)) {
    return { key: 'bannerStatus.soldOut', isActive: false };
  }

  if (isTimedRelease(product) || isBlockchainAuctionRelease(product)) {
    if (isUpcoming(product)) {
      let statusKey = 'bannerStatus.timed.upcoming.date';

      if (isInNextSixDays(new Date(product.date))) {
        statusKey = 'bannerStatus.timed.upcoming.countdown.long';
      }

      if (isInNext24Hours(new Date(product.date))) {
        statusKey = 'bannerStatus.timed.upcoming.countdown.short';
      }

      return { key: statusKey, isActive: false };
    }

    if (isAvailable(product)) {
      if (isBlockchainAuctionRelease(product)) {
        const statusKey = 'bannerStatus.auction.active';
        return { key: statusKey, isActive: true };
      }

      if (product.closingDate) {
        let statusKey = 'bannerStatus.timed.active.long';

        if (isInNext24Hours(new Date(product.closingDate))) {
          statusKey = 'bannerStatus.timed.active.short';
        }
        return { key: statusKey, isActive: true };
      }

      return { key: 'bannerStatus.available.default', isActive: true };
    }
  }

  if (isDrawOrAuction) {
    if (isUpcoming(product)) {
      if (isPreOrder) {
        return { key: 'bannerStatus.preOrder.upcoming.date', isActive: true };
      }

      let statusKey = 'bannerStatus.draw.upcoming.date';

      if (isInNextSixDays(new Date(product.date))) {
        statusKey = 'bannerStatus.draw.upcoming.countdown.long';
      }

      if (isInNext24Hours(new Date(product.date))) {
        statusKey = 'bannerStatus.draw.upcoming.countdown.short';
      }

      return { key: statusKey, isActive: true };
    }
  }

  if (isPrivateRelease(product)) {
    if (product.fewLeft) {
      return { key: 'bannerStatus.private.fewLeft', isActive: true };
    }
    return { key: 'bannerStatus.private.default', isActive: true };
  }

  if (isUpcoming(product)) {
    let statusKey = 'bannerStatus.upcoming.date';

    if (isInNextSixDays(new Date(product.date))) {
      statusKey = 'bannerStatus.upcoming.countdown.long';
    }

    if (isInNext24Hours(new Date(product.date))) {
      statusKey = 'bannerStatus.upcoming.countdown.short';
    }

    return { key: statusKey, isActive: false };
  }

  if (isAvailable(product)) {
    let statusKey = 'bannerStatus.available.default';

    if (product.fewLeft) {
      statusKey = 'bannerStatus.available.fewLeft';
    }

    return { key: statusKey, isActive: true };
  }

  return null;
};

export const getUpcomingDescription = (product: Product): string => {
  const isDrawOrAuction =
    isDrawRelease(product.releaseType) || isAuctionRelease(product.releaseType);

  if (isTimedRelease(product)) {
    if (isInNextSixDays(new Date(product.date))) {
      return 'upcomingDescription.timed.short';
    }
    return 'upcomingDescription.timed.long';
  }

  if (isDrawOrAuction) {
    if (isInNextSixDays(new Date(product.date))) {
      return 'upcomingDescription.draw.short';
    }
    return 'upcomingDescription.draw.long';
  }

  if (isBlockchainAuctionRelease(product)) {
    return 'upcomingDescription.auction';
  }

  if (isInNextSixDays(new Date(product.date))) {
    return 'upcomingDescription.default.short';
  }
  return 'upcomingDescription.default.long';
};

type ProductTileStatusText = {
  variant: 'text';
  isActive: boolean;
  key: string;
};

type ProductTileStatusDate = {
  variant: 'date';
  isActive: boolean;
  date: Date;
  key: string;
};

export type ProductTileStatusResult =
  | ProductTileStatusText
  | ProductTileStatusDate;

export const getProductTileStatus = (
  product: Product,
  isPreOrder?: boolean,
  draw?: GeneratedGetDrawByProductIDsQuery['getDrawByProductIDs'][number],
): ProductTileStatusResult | null => {
  const timedRelease = isTimedRelease(product);
  const isBlockchainAuction = isBlockchainAuctionRelease(product);

  if (isAvailable(product) && !timedRelease && !isBlockchainAuction)
    return null;

  const tilePill = getProductTilePill(product, isPreOrder, draw);

  if (tilePill === null) return null;

  if (isSoldOut(product)) {
    return { isActive: false, key: 'tileStatus.soldOut', variant: 'text' };
  }

  const isDrawOrAuction =
    isDrawRelease(product.releaseType) || isAuctionRelease(product.releaseType);

  if (!tilePill.isActive) {
    let date = new Date();
    if (isDrawOrAuction && draw?.openAt) date = new Date(draw.openAt);
    else if (product.date) date = new Date(product.date);
    const isEvent = timedRelease || isBlockchainAuction || isDrawOrAuction;
    return {
      key: isEvent ? 'countdown.starting' : 'countdown.releasing',
      date,
      isActive: false,
      variant: 'date',
    };
  }

  if (isPreOrder) {
    const date = product.closingDate
      ? new Date(product.closingDate)
      : new Date(product.launchDate ?? '');
    return {
      key: 'countdown.releasing-preorder',
      date,
      isActive: tilePill.isActive,
      variant: 'date',
    };
  }

  let date = new Date();
  if (product.closingDate) date = new Date(product.closingDate);
  else if (product.launchDate) date = new Date(product.launchDate);
  if (isDrawOrAuction && draw?.closeAt) date = new Date(draw.closeAt);

  if (isBlockchainAuction && (isToday(date) || isTomorrow(date))) {
    return {
      isActive: tilePill.isActive,
      key: isToday(date) ? 'auction.closingToday' : 'auction.closingTomorrow',
      variant: 'text',
    };
  }

  return {
    key: timedRelease ? 'countdown.ending' : 'countdown.closing',
    date,
    isActive: tilePill.isActive,
    variant: 'date',
  };
};
